import { useCallback, useEffect, useState, type ReactNode } from 'react'
import {
  PRODUCT_LAW_KEYS,
  useDiscoverV2Product,
  useDiscoverV2ProductLaw,
  useGetProductApplicability,
  useInvalidateOnDiscoverWorkflowTerminal,
  useProductDiscoverWorkflowLatest,
  type ProductLawKey,
} from '@/shared/hooks/api/product'
import { V2ProductDiscoverScopeChamber } from '../../components/V2ProductDiscoverScopeChamber'
import { V2ProductRegulationSection } from '../../components/V2ProductRegulationSection'
import { isProductDiscoverWorkflowRunning } from '../../utils/productDiscoverWorkflow'

type V2ProductRegulationPageShellProps = {
  productId: string
  law: ProductLawKey
  children: (applies: boolean) => ReactNode
}

export function V2ProductRegulationPageShell({
  productId,
  law,
  children,
}: V2ProductRegulationPageShellProps) {
  const { data: applicability, isLoading: isLoadingApplicability } =
    useGetProductApplicability(productId)
  const discoverMutation = useDiscoverV2Product(productId)
  const discoverLawMutation = useDiscoverV2ProductLaw(productId)
  const { data: discoverWorkflow } = useProductDiscoverWorkflowLatest(productId)
  useInvalidateOnDiscoverWorkflowTerminal(productId, discoverWorkflow)
  const [discoverSheetOpen, setDiscoverSheetOpen] = useState(false)
  const anyLawRunning = PRODUCT_LAW_KEYS.some((productLaw) => {
    return applicability?.[productLaw]?.runState === 'running'
  })
  const discoverWorkflowRunning = isProductDiscoverWorkflowRunning(
    discoverWorkflow
  )

  useEffect(() => {
    if (discoverWorkflowRunning) {
      setDiscoverSheetOpen(true)
    }
  }, [discoverWorkflowRunning])

  const handleRunLawDiscover = useCallback(
    (productLaw: ProductLawKey) => {
      setDiscoverSheetOpen(true)
      if (discoverLawMutation.isPending || discoverWorkflowRunning) {
        return
      }
      discoverLawMutation.mutate(productLaw)
    },
    [discoverLawMutation, discoverWorkflowRunning]
  )

  const isDiscoverRunning =
    discoverMutation.isPending ||
    discoverLawMutation.isPending ||
    discoverWorkflowRunning ||
    anyLawRunning
  const lawApplicability = applicability?.[law] ?? null
  const showRunningOverlay =
    lawApplicability?.runState === 'running' ||
    (law === 'rohs' && discoverMutation.isPending)
  const applies = lawApplicability?.status === 'APPLICABLE'

  return (
    <div className="space-y-6">
      <V2ProductDiscoverScopeChamber
        productId={productId}
        workflow={discoverWorkflow}
        open={discoverSheetOpen}
        onOpenChange={setDiscoverSheetOpen}
      />

      <V2ProductRegulationSection
        productId={productId}
        law={law}
        applicability={lawApplicability}
        isLoading={isLoadingApplicability}
        showRunningOverlay={showRunningOverlay}
        actionsDisabled={isDiscoverRunning}
        isLawDiscoverPending={discoverLawMutation.isPending}
        onRunLawDiscover={handleRunLawDiscover}
      >
        {children(applies)}
      </V2ProductRegulationSection>
    </div>
  )
}
